import type {BoardCell, ItemId} from '../types'

/**
 * Which item in a cell carries which id, per cell, built on first ask and kept.
 *
 * The one index over the caller's data that is not already a `Map` lookup, and the only
 * way anything inside the package reaches the caller's own item from an address. Two
 * things need it and neither could build its own: `selection/card-detail.ts` resolves a
 * selected card, and `cells/use-board-card.ts` asks the caller whether a card may be
 * dragged. A second index built per card would be a scan of the cell per drawn card.
 *
 * Held beside the config rather than inside it, because the two go stale on different
 * things. What a cell holds changes only when the caller's data does, while the config
 * around it is rebuilt whenever an accessor or a label changes identity: accessors are
 * usually stated inline, and one can close over the caller's own state. An index tied to
 * the config would be rebuilt for reasons that say nothing about which item carries which
 * id, and a controlled selection update re-renders the caller, so that lands on the
 * interaction path.
 */
export type BoardCellItemIndex = {
  /**
   * The item in one cell carrying one id, or absent when that cell holds no such card.
   *
   * `getItemId` is passed at the call rather than captured at the build, so a config that
   * is only a new object reads the index already built. An accessor that really answered
   * differently would change the ids in the caller's own selection too, and a selection
   * and the cards it names move together as new data.
   */
  itemIn: (cellKey: string, itemId: ItemId, getItemId: (item: unknown) => ItemId) => unknown
}

/**
 * Builds the index over one set of cells.
 *
 * Nothing is read until a card is resolved, and then only in the cell that card is in, so
 * a caller reading `count` alone never pays for a scan it did not ask for: a board polling
 * with six hundred cards selected would otherwise rebuild an index per poll for a caller
 * drawing one number.
 */
export function createCellItemIndex(cellsByAddress: ReadonlyMap<string, BoardCell<unknown>>): BoardCellItemIndex {
  // Keyed by `composeCellKey`, the same string `cellsByAddress` is keyed by, so the two
  // cannot disagree about which cell an entry belongs to.
  const itemsByCell = new Map<string, ReadonlyMap<ItemId, unknown>>()

  function itemsIn(
    cell: BoardCell<unknown>,
    cellKey: string,
    getItemId: (item: unknown) => ItemId,
  ): ReadonlyMap<ItemId, unknown> {
    const built = itemsByCell.get(cellKey)
    if (built !== undefined) return built

    const items = new Map(cell.items.map(item => [getItemId(item), item]))
    itemsByCell.set(cellKey, items)

    return items
  }

  return {
    itemIn: (cellKey, itemId, getItemId) => {
      const cell = cellsByAddress.get(cellKey)

      return cell === undefined ? undefined : itemsIn(cell, cellKey, getItemId).get(itemId)
    },
  }
}
